import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { CallPOSTAPI } from "../helper/Constants";

function Register() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rePassword, setRePassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = () => {
    const nextErrors = {};

    if (!firstName.trim()) {
      nextErrors.firstName = "First name is required";
    }

    if (!lastName.trim()) {
      nextErrors.lastName = "Last name is required";
    }

    if (!email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      nextErrors.email = "Email address is invalid";
    }

    if (!password.trim()) {
      nextErrors.password = "Password is required";
    }

    if (!rePassword.trim()) {
      nextErrors.rePassword = "Please re-enter your password";
    } else if (password !== rePassword) {
      nextErrors.rePassword = "Passwords do not match";
    }

    return nextErrors;
  };

  const clearError = (field) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      setLoading(true);

      const response = await CallPOSTAPI("api/admin-create", {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        repassword: rePassword,
      });

      if (response?.data?.status) {
        toast.success("Register successful");
        navigate("/");
      } else {
        toast.error(response?.data?.message || "Unable to create account");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong, please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 ${loading ? "opacity-90" : ""}`}>
      <ToastContainer />
      <main className="flex min-h-screen items-center justify-center px-4 py-8">
        <div className="w-full max-w-xl">
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
            <div className="px-8 py-8">
              <div className="mb-8 text-center">
                <h1 className="mb-2 text-3xl font-bold text-gray-900">Create Your Account</h1>
                <p className="text-sm text-gray-600">
                  Enter your personal details to create your Calladda admin account
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="mb-2 block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        clearError("firstName");
                      }}
                      className={`w-full rounded-lg border px-4 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.firstName ? "border-red-400" : "border-gray-300"
                      }`}
                      placeholder="Enter first name"
                    />
                    {errors.firstName ? <p className="mt-2 text-sm text-red-600">{errors.firstName}</p> : null}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="mb-2 block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        clearError("lastName");
                      }}
                      className={`w-full rounded-lg border px-4 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.lastName ? "border-red-400" : "border-gray-300"
                      }`}
                      placeholder="Enter last name"
                    />
                    {errors.lastName ? <p className="mt-2 text-sm text-red-600">{errors.lastName}</p> : null}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearError("email");
                    }}
                    className={`w-full rounded-lg border px-4 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.email ? "border-red-400" : "border-gray-300"
                    }`}
                    placeholder="Enter email address"
                  />
                  {errors.email ? <p className="mt-2 text-sm text-red-600">{errors.email}</p> : null}
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearError("password");
                      }}
                      className={`w-full rounded-lg border py-2.5 pl-4 pr-12 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.password ? "border-red-400" : "border-gray-300"
                      }`}
                      placeholder="Create a password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 transition-colors hover:text-gray-700"
                    >
                      {showPassword ? <FaEye className="h-5 w-5" /> : <FaEyeSlash className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password ? <p className="mt-2 text-sm text-red-600">{errors.password}</p> : null}
                </div>

                <div>
                  <label htmlFor="rePassword" className="mb-2 block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="rePassword"
                      type={showRePassword ? "text" : "password"}
                      value={rePassword}
                      onChange={(e) => {
                        setRePassword(e.target.value);
                        clearError("rePassword");
                      }}
                      className={`w-full rounded-lg border py-2.5 pl-4 pr-12 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.rePassword ? "border-red-400" : "border-gray-300"
                      }`}
                      placeholder="Re-enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowRePassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 transition-colors hover:text-gray-700"
                    >
                      {showRePassword ? <FaEye className="h-5 w-5" /> : <FaEyeSlash className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.rePassword ? <p className="mt-2 text-sm text-red-600">{errors.rePassword}</p> : null}
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-3 font-medium text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-300"
                  >
                    {loading ? (
                      <>
                        <svg
                          className="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </button>
                </div>

                <div className="text-center pt-2">
                  <p className="text-sm text-gray-600">
                    Already have an account?{" "}
                    <Link to="/" className="font-medium text-blue-600 transition-colors hover:text-blue-700 hover:underline">
                      Log in
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">© 2024 Calladda. All rights reserved.</p>
          </div>
        </div>
      </main>

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-center space-x-3">
              <svg className="h-8 w-8 animate-spin text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="font-medium text-gray-700">Processing...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Register;
